// hooks/useAddBookingForm.ts
import { useRef, useState } from "react";
import { toast } from "react-toastify";
import { add, format } from "date-fns";
import {
  checkAvailability,
  createBooking,
  getCalendarDays,
} from "../services/api";
import { validateEmail } from "../lib/validateEmail";

interface GlamPackage {
  title: string;
  id: string;
  name: string;
  price: number;
}

interface UseAddBookingFormProps {
  onBack?: () => void;
  duration?: { hours: number; minutes: number }; // Add duration prop
}

export function useAddBookingForm({
  onBack,
  duration = { hours: 4, minutes: 0 },
}: UseAddBookingFormProps = {}) {
  const [email, setEmail] = useState("");
  const [glamPackages, setGlamPackages] = useState<GlamPackage[]>([]);
  const [selectedPackage, setSelectedPackage] = useState<string>("");
  const [customerName, setCustomerName] = useState("");
  const [glamSpot, setGlamSpot] = useState("");
  const [homeAddress, setHomeAddress] = useState("");
  const [landmark, setLandmark] = useState("");
  const [numberOfFaces, setNumberOfFaces] = useState("");
  const [glamTime, setGlamTime] = useState("");
  const [glamDate, setGlamDate] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [clientPhoto, setClientPhoto] = useState<File | null>(null);
  const [clientPhotoPreview, setClientPhotoPreview] = useState<string | null>(
    null
  );
  const [showCustomFacesInput, setShowCustomFacesInput] = useState(false);
  const [customFacesCount, setCustomFacesCount] = useState("");

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  const [originalDate, setOriginalDate] = useState("");
  const [originalTime, setOriginalTime] = useState("");
  // Date selection state
  const [dateSelection, setDateSelection] = useState({
    selectedDate: "",
    isAvailable: false,
    isLoading: false,
  });

  // Time selection state
  const [timeSelection, setTimeSelection] = useState({
    selectedTime24h: "",
    selectedTimeDisplay: "",
    isAvailable: false,
    isLoading: false,
  });

  const handleNumberOfFacesChange = (value: string) => {
    setNumberOfFaces(value);
    setShowCustomFacesInput(value === "more");
    if (value !== "more") {
      setCustomFacesCount("");
    }
  };

  const handleCustomFacesChange = (value: string) => {
    // Ensure only numbers are entered
    const numericValue = value.replace(/\D/g, "");
    setCustomFacesCount(numericValue);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);

    // Clear error when user starts typing
    if (emailError) setEmailError(null);
  };

  const handleDateSelect = (date: string) => {
    if (!date) return;
  
    // date is already in yyyy-MM-dd format from the date picker
    const formattedDate = date;
  
    // Update date selection state
    setDateSelection({
      selectedDate: date,
      isLoading: false,
      isAvailable: true, // Assume all dates are available
    });
  
    // Update glam date
    setGlamDate(formattedDate);
  
    // Clear time selection when date changes
    setTimeSelection({
      selectedTime24h: "",
      selectedTimeDisplay: "",
      isAvailable: false,
      isLoading: false,
    });
    setGlamTime("");
  };

  const handleTimeSelect = async (time24h: string) => {
    if (!dateSelection.selectedDate || !time24h) return;

    setTimeSelection((prev) => ({
      ...prev,
      selectedTime24h: time24h,
      isLoading: true,
      isAvailable: time24h === originalTime,
    }));

    // Skip availability check if time hasn't changed
    if (time24h === originalTime) {
      setTimeSelection((prev) => ({
        ...prev,
        isLoading: false,
        isAvailable: true,
      }));
      return;
    }

    try {
      // dateSelection.selectedDate is already in yyyy-MM-dd format
      const formattedDate = dateSelection.selectedDate;
      const startTime = `${time24h}:00`;

      // Calculate number of faces (use custom count if "more" is selected)
      const facesCount =
        numberOfFaces === "more"
          ? parseInt(customFacesCount || "1", 10)
          : parseInt(numberOfFaces || "1", 10);

      // Ensure facesCount is at least 1
      const adjustedFacesCount = Math.max(1, facesCount);

      // Calculate adjusted duration based on face count
      const adjustedDuration = {
        hours: duration.hours * adjustedFacesCount,
        minutes: duration.minutes * adjustedFacesCount,
      };

      // Create full datetime strings with adjusted duration
      const startDateTime = new Date(`${formattedDate} ${startTime}`);
      const endDateTime = add(startDateTime, {
        hours: adjustedDuration.hours,
        minutes: adjustedDuration.minutes,
      });

      // Format for API request
      const startDateTimeString = format(startDateTime, "yyyy-MM-dd HH:mm:ss");
      const endDateTimeString = format(endDateTime, "yyyy-MM-dd HH:mm:ss");

      // Check availability with full datetime strings
      const response = (await checkAvailability({
        start_time: startDateTimeString,
        end_time: endDateTimeString,
      })) as any;

      const displayTime = format(new Date(`2000-01-01 ${startTime}`), "h:mm a");
      const isAvailable = response.data.available === true;

      setTimeSelection((prev) => ({
        ...prev,
        selectedTimeDisplay: displayTime,
        isAvailable,
        isLoading: false,
        endTime: format(endDateTime, "h:mm a"),
        endTime24h: format(endDateTime, "HH:mm"),
      }));

      if (isAvailable) {
        toast.success(response.message || "Time slot available!", {
          position: "top-center",
          autoClose: 3000,
        });
      } else {
        toast.error(response.message || "Time slot unavailable", {
          position: "top-center",
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Availability check failed:", error);
      toast.error("Failed to check availability", {
        position: "top-center",
        autoClose: 3000,
      });
      setTimeSelection((prev) => ({
        ...prev,
        isLoading: false,
        isAvailable: false,
      }));
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      setClientPhoto(file);
      setClientPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async () => {
    // Validate email first
    if (email && !validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    if (glamSpot === "home" && !homeAddress.trim()) {
      toast.error("Home address is required for home service");
      return;
    }

    if (
      !customerName ||
      !selectedPackage ||
      !glamSpot ||
      !numberOfFaces ||
      !email ||
      !timeSelection.selectedTime24h ||
      !dateSelection.selectedDate ||
      !phoneNumber
    ) {
      toast.error("Please fill in all required fields.", {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }

    if (!timeSelection.isAvailable) {
      toast.error(
        "Selected time slot is not available. Please choose another time.",
        {
          position: "top-center",
          autoClose: 3000,
        }
      );
      return;
    }

    const finalNumberOfFaces =
      numberOfFaces === "more" ? customFacesCount : numberOfFaces;

    try {
      // dateSelection.selectedDate is already in yyyy-MM-dd format
      const formattedDate = dateSelection.selectedDate;
      const startTime = `${timeSelection.selectedTime24h}:00`;

      // Calculate adjusted duration (same logic as in handleTimeSelect)
      const facesCount =
        numberOfFaces === "more"
          ? parseInt(customFacesCount || "1", 10)
          : parseInt(numberOfFaces || "1", 10);
      const adjustedFacesCount = Math.max(1, facesCount);
      const adjustedDuration = {
        hours: duration.hours * adjustedFacesCount,
        minutes: duration.minutes * adjustedFacesCount,
      };

      // Create full datetime strings with adjusted duration
      const startDateTime = new Date(`${formattedDate} ${startTime}`);
      const endDateTime = add(startDateTime, {
        hours: adjustedDuration.hours,
        minutes: adjustedDuration.minutes,
      });

      // Format for API submission
      const appointmentStartDate = formattedDate;
      const appointmentEndDate = formattedDate; // Same day
      const appointmentStartDateTime = format(
        startDateTime,
        "yyyy-MM-dd HH:mm:ss"
      );
      const appointmentEndDateTime = format(endDateTime, "yyyy-MM-dd HH:mm:ss");

      const formData = new FormData() as any;
      formData.append("number_of_faces", finalNumberOfFaces);
      formData.append("booking_start_date", appointmentStartDate);
      formData.append("booking_end_date", appointmentEndDate);
      formData.append("booking_start_date_time", appointmentStartDateTime);
      formData.append("booking_end_date_time", appointmentEndDateTime);
      formData.append(
        "glam_spot",
        glamSpot === "studio" ? "studio_session" : "home_service"
      );
      formData.append("glam_package_id", selectedPackage);
      formData.append("first_experience", "yes");
      formData.append("email_address", email);
      formData.append("phone_number", phoneNumber);
      formData.append("name", customerName);
      if (additionalInfo && additionalInfo.trim() !== "") {
        formData.append("additional_info", additionalInfo);
      }
      if (clientPhoto) {
        formData.append("client_photo", clientPhoto);
      }

      if (glamSpot === "home") {
        formData.append("home_address", homeAddress);
        if (landmark.trim()) {
          formData.append("landmark", landmark);
        }
      }

      const response = await createBooking(formData);
      toast.success("Booking created successfully!", {
        position: "top-center",
        autoClose: 3000,
      });

      if (onBack) {
        onBack();
      }
    } catch (error) {
      console.error("Failed to create booking:", error);
      toast.error("Failed to create booking.", {
        position: "top-center",
        autoClose: 3000,
      });
    }
  };

  return {
    glamPackages,
    clientPhotoPreview,
    customerName,
    selectedPackage,
    glamSpot,
    numberOfFaces,
    showCustomFacesInput,
    customFacesCount,
    clientPhoto,
    email,
    emailError,
    phoneNumber,
    additionalInfo,
    handleEmailChange,
    setEmail,
    setClientPhotoPreview,
    setGlamPackages,
    setSelectedPackage,
    setCustomerName,
    setGlamSpot,
    handleNumberOfFacesChange,
    handleCustomFacesChange,
    setEmailError,
    setPhoneNumber,
    setAdditionalInfo,
    fileInputRef,
    dateSelection,
    timeSelection,
    setTimeSelection,
    setDateSelection,
    setOriginalDate,
    setOriginalTime,
    handleDateSelect,
    handleTimeSelect,
    validateEmail,
    handleImageUpload,
    handleSubmit,homeAddress,
    setHomeAddress,
    landmark,
    setLandmark,
  };
}
